// Concern pages for the district dashboard — thin wrappers that give each of the
// dashboard's graphics a URL of its own. The heavy lifting stays in
// containers/impact/tabs/*; these pages only add page-level controls and the
// concern framing.
import React, { useState, useEffect, useMemo } from "react";
import { FocusTab } from "../impact/tabs/FocusTab";
import { EngagementTab } from "../impact/tabs/EngagementTab";
import { YearUsagePanel } from "../impact/tabs/YearUsagePanel";
import { RobotsTab } from "../impact/tabs/RobotsTab";
import { UnitMasteryTab } from "../impact/tabs/UnitMasteryTab";
import { GroupGrowthTab } from "../impact/tabs/GroupGrowthTab";
import {
  BreakdownControl,
  SchoolSelect,
  PeriodSelect,
  useFocusCycles,
} from "../impact/chrome";
import { SubTabs, ControlBar } from "../impact/ui";
import {
  toSegmentRows,
  buildCohortRows,
  cycleBandsForBuckets,
  COHORT_PEER_STATES,
  refById,
  refConst,
} from "../impact/model";

// Breakdown-dimension controls shared by the pages that fan out over segments.
//
// `inline` renders them for a page ControlBar — labels beside their controls, no
// block of its own. Without it they keep the stacked micro-cap treatment and own
// their row, which is still how the Learning page uses them.
const SegmentControls = ({
  dimension,
  setDimension,
  orgunits,
  selectedSchool,
  setSelectedSchool,
  inline,
  available,
  unavailableReason,
}) => (
  <div
    style={{
      // flex-end so the pill trays line up along their bottoms — the stacked
      // micro-cap labels above them differ in height when one wraps.
      display: "flex",
      alignItems: inline ? "center" : "flex-end",
      gap: inline ? "8px 18px" : "12px 22px",
      flexWrap: "wrap",
      marginBottom: inline ? 0 : 16,
    }}
  >
    <BreakdownControl
      dimension={dimension}
      setDimension={setDimension}
      inline={inline}
      available={available}
      unavailableReason={unavailableReason}
    />
    {dimension === "teacher" && (
      <SchoolSelect
        schools={orgunits}
        value={selectedSchool}
        onChange={setSelectedSchool}
        inline={inline}
      />
    )}
  </div>
);

// Level captions. Each says what the rows are and what they're measured
// against, because the reference changes at every level and between the two
// paths — that's the only thing a reader can't infer from the numbers.
const GRADE_CAPTION =
  "Grade cohorts at this school, each compared with the same grade across the " +
  "district";
const TEACHER_CAPTION =
  "Teachers holding this cohort, each compared with the cohort as a whole";
const SCHOOL_IN_GRADE_CAPTION =
  "Schools with learners in this cohort, each compared with the cohort " +
  "district-wide";
const TEACHER_IN_SCHOOL_CAPTION =
  "Teachers holding this cohort at this school, compared with the school's " +
  "share of it";

// A teacher contributing a single learner to a grade cohort is nearly always a
// rostering artifact — one learner parked in a class that belongs to another
// grade — and a one-learner reach/depth pair is an anecdote, not a finding.
// Dropped from the table, but counted in a footnote so nothing vanishes
// silently.
const MIN_LEAF_ROSTER = 2;

// Response cache for the drill levels, deliberately at module scope rather than
// in a ref. DistrictDashboard replaces the whole route body with a spinner while
// it reloads its worklist, which unmounts the Outliers lens — so a ref is wiped on
// every breakdown switch and every page-to-page navigation, and each reopened
// level refetches. Keyed by (org, window, standardset) plus the exact request
// params, which is what lets the two drill paths share the leaf call they have
// in common. FIFO-bounded so a session spent switching orgs and years can't
// grow it without limit.
//
// Only drill panels read this; the top-level worklist is loaded by
// DistrictDashboard on its own and stays as fresh as the page.
const DRILL_CACHE = new Map();
const DRILL_CACHE_MAX = 200;
const drillCached = (ck, make) => {
  if (!DRILL_CACHE.has(ck)) {
    if (DRILL_CACHE.size >= DRILL_CACHE_MAX)
      DRILL_CACHE.delete(DRILL_CACHE.keys().next().value);
    DRILL_CACHE.set(
      ck,
      make().catch((err) => {
        DRILL_CACHE.delete(ck); // let a re-open retry
        throw err;
      })
    );
  }
  return DRILL_CACHE.get(ck);
};

// Learners with no recorded grade. The backend emits one bucket for them
// (`f'grade:{g}'` with g=None) because at district level "N learners have no
// grade on file" is real roster-hygiene information — but inside a single
// school's cohort table it's a noise row sitting between actual grades, so the
// drill-down drops it. Deliberately scoped to the drill-down: the "Break down
// by → Grade" worklist still shows it.
const GRADE_UNKNOWN_ID = "grade:None";

// Two-level drill-downs for the worklist, in whichever order the breakdown
// implies:
//
//   Break down by School →  school → grade cohort → teacher
//   Break down by Grade  →  grade cohort → school → teacher
//
// Both are the same three population slices reached from different ends, and
// the backend models that directly: `dimension` says how to split, and
// school/grade/teacher are *filters* that narrow what gets split (see
// vanroboapp/segments/services.py). So each level is one call —
//
//   dimension=grade&school=<ou>              a school's cohorts
//   dimension=grade                          the district-wide cohorts
//   dimension=school&grade=<g>               a cohort's schools
//   dimension=teacher&school=<ou>&grade=<g>  the teachers in one (school, cohort)
//
// — and the leaf call is literally identical on both paths, so opening it from
// either end is a cache hit on the other. Responses are memoized on their exact
// params for the life of the org + window selection: the district reference is
// fetched at most once however many rows are opened, each level at most once,
// and collapse/re-expand is free.
//
// Returns null (no drill affordance at all) when the breakdown is neither
// schools nor grades. Past years are fine at every level: school enrollment,
// class enrollment and grade are all resolved for the window from the interval
// ledger (see vanroboapp/models/enrollment.py), not read off current state.
//
// Which grades appear is entirely data-driven: the backend buckets the actual
// roster, so PreK shows up exactly when there are PreK learners, and nothing
// assumes a K–5 span.
//
// The peer reference changes at every level, which is why buildCohortRows takes
// a resolver:
//   • a grade cohort inside a school → the same grade district-wide (refById).
//     A K cohort can only be read against other K cohorts.
//   • everything else → the row it was expanded from (refConst): a part against
//     the whole it belongs to. It's the right question at those levels ("which
//     school/room is holding this cohort back?") and it's free, since the
//     parent row is already on screen.
const useSegmentDrill = ({ api, org, windowSpec, dimension }) => {
  const orgId = org && org.id;
  const { start, end, interval, ssParam } = windowSpec;
  const key = `${orgId}|${start}|${end}|${interval}|${ssParam || ""}`;

  return useMemo(() => {
    if (!orgId) return null;
    if (dimension !== "school" && dimension !== "grade") return null;

    // One memo for every request this hook makes, keyed by the params
    // themselves — which is what lets the two paths share their leaf call.
    const fetchSegments = (params) =>
      drillCached(`${key}|${JSON.stringify(params)}`, () =>
        api
          .fetchSegmentedWindowedImpactStats({
            id: orgId,
            start,
            end,
            interval,
            standardset: ssParam,
            ...params,
          })
          .then((r) => toSegmentRows(r && r.segments))
      );
    const idPart = (row) => String(row.id).split(":")[1];
    const cohortsAt = (school) =>
      fetchSegments({ dimension: "grade", school }).then((rows) =>
        rows.filter((s) => s.id !== GRADE_UNKNOWN_ID)
      );

    // Teachers in one (school, cohort) — the leaf of both paths. `against` is
    // whichever row sits directly above it, which differs by path.
    //
    // One row per teacher, not per class: the backend attributes each class to
    // the userprofiles linked to it and unions a teacher's classes together, so a
    // teacher running three sections of the same grade is one row — which is why
    // the rows are labelled by teacher and not by classroom.
    const loadTeachers = (school, cohort, against) =>
      fetchSegments({
        dimension: "teacher",
        school: idPart(school),
        grade: idPart(cohort),
      }).then((all) => {
        const withRoster = all.filter((s) => s.roster > 0);
        const kept = withRoster.filter((s) => s.roster >= MIN_LEAF_ROSTER);
        const hidden = withRoster.length - kept.length;
        return {
          rows: buildCohortRows(kept, refConst(against)),
          note: hidden
            ? `${hidden} teacher${hidden > 1 ? "s" : ""} with a single ` +
              `${cohort.name} learner hidden.`
            : null,
        };
      });

    // Shared leaf prose. `cohortName` is threaded through the row rather than
    // closed over because one descriptor serves every parent row.
    const teacherLevel = (cohortNameOf, refNameOf) => ({
      key,
      colLabel: "Teacher",
      compareLabel: "vs. grade cohort",
      emptyText: "No teacher here holds more than one learner in this grade.",
      noPeerLabel: { sole: "Whole cohort", idle: "No cohort activity" },
      refName: refNameOf,
      noPeerHelp: (c, parent) => {
        const g = cohortNameOf(parent);
        if (c.peerState === COHORT_PEER_STATES.sole)
          return `This teacher holds ${c.roster} of the ${c.peerRoster} ${g} learners it sits inside, so the comparison would be the teacher against itself.`;
        if (c.peerState === COHORT_PEER_STATES.idle)
          return `No ${g} learner here has engaged this school year, so there is nothing to compare against — not a sign this teacher is doing well.`;
        return `There is no ${g} cohort to compare this teacher against.`;
      },
    });

    if (dimension === "school") {
      // school → grade cohort → teacher
      //
      // The district reference is shared across every school and isn't worth
      // failing an expansion over: on error fall back to no reference, which
      // renders the cohorts with their numbers and "No peer cohort" statuses.
      const districtRef = () => cohortsAt(undefined).catch(() => []);
      return {
        key,
        load: (school) =>
          Promise.all([cohortsAt(idPart(school)), districtRef()]).then(
            ([cohorts, ref]) => buildCohortRows(cohorts, refById(ref))
          ),
        colLabel: "Grade",
        caption: GRADE_CAPTION,
        emptyText: "No grade cohorts with an enrolled roster at this school.",
        refName: (c) => `district ${c.name}`,
        noPeerHelp: (c, school) => {
          if (c.peerState === COHORT_PEER_STATES.sole)
            return `${school.name} holds ${c.roster} of the district's ${c.peerRoster} ${c.name} learners, so "vs. district" would be this cohort against itself.`;
          if (c.peerState === COHORT_PEER_STATES.idle)
            return `No ${c.name} learner anywhere in the district has engaged this school year, so there is nothing to compare against — not a sign this cohort is doing well.`;
          return `The district has no ${c.name} cohort to compare against.`;
        },
        child: {
          ...teacherLevel(
            (cohort) => cohort.name,
            (c, cohort) => `this school's ${cohort.name}`
          ),
          load: (cohort, school) => loadTeachers(school, cohort, cohort),
          caption: TEACHER_CAPTION,
        },
      };
    }

    // grade cohort → school → teacher. Every row at both levels is the same
    // grade, so there's no grade confounding to correct for: each row is simply
    // its share of the cohort above it.
    return {
      key,
      load: (cohort) =>
        fetchSegments({ dimension: "school", grade: idPart(cohort) }).then(
          (rows) =>
            buildCohortRows(
              rows.filter((s) => s.roster > 0),
              refConst(cohort)
              // carried so the teacher level below can name the cohort it is
              // two levels under; it only ever sees its immediate parent.
            ).map((r) => ({ ...r, cohortName: cohort.name }))
        ),
      // "Grade unknown" is a roster-hygiene bucket, not a cohort — the backend
      // won't filter by it and there is nothing coherent to break it down into.
      canExpand: (row) => row.id !== GRADE_UNKNOWN_ID,
      colLabel: "School",
      caption: SCHOOL_IN_GRADE_CAPTION,
      compareLabel: "vs. district cohort",
      emptyText: "No school has an enrolled roster in this cohort.",
      noPeerLabel: { sole: "Whole cohort", idle: "No cohort activity" },
      refName: (c, cohort) => `district ${cohort.name}`,
      noPeerHelp: (c, cohort) => {
        if (c.peerState === COHORT_PEER_STATES.sole)
          return `${c.name} holds ${c.roster} of the district's ${c.peerRoster} ${cohort.name} learners, so the comparison would be this school against itself.`;
        if (c.peerState === COHORT_PEER_STATES.idle)
          return `No ${cohort.name} learner anywhere in the district has engaged this school year, so there is nothing to compare against — not a sign this school is doing well.`;
        return `There is no district-wide ${cohort.name} cohort to compare against.`;
      },
      child: {
        ...teacherLevel(
          (school) => school.cohortName || "cohort",
          (c, school) => `${school.name}'s ${school.cohortName || "cohort"}`
        ),
        load: (school, cohort) => loadTeachers(school, cohort, school),
        caption: TEACHER_IN_SCHOOL_CAPTION,
        compareLabel: "vs. school cohort",
      },
    };
    // `api` is re-created every render by withAPI; start/end/interval/ssParam
    // are all folded into `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, key, dimension]);
};

// Adoption → Outliers: the reach × depth quadrant + worklist.
// Worklist rows expand two levels deep, in whichever order the breakdown
// implies: schools into their grade cohorts into teachers, or grade cohorts
// into their schools into teachers (useSegmentDrill).
//
// Deliberately NOT scopeable to a focus cycle, though the Usage lens next door
// is. Cycle filtering is only justified where the claim is a rate measured
// against a recommendation (dose→response, at-target share), because there the
// year-average denominator is the thing that is actually wrong. Reach and depth
// are comparative, and inside a cycle they would silently change meaning — reach
// would become "share of roster reached *during the push*" — while the σ-ranking
// would be computed over a window selected precisely because usage was dense in
// it. Cycles appear here as annotation instead (the sparkline band), which costs
// no sample size.
const OutliersLens = ({
  quad,
  interval,
  noun,
  api,
  org,
  windowSpec,
  dimension,
  robotsFor,
}) => {
  const drill = useSegmentDrill({ api, org, windowSpec, dimension });
  // Cycles as annotation, never as a filter here — see the note above.
  const cycles = useFocusCycles({
    api,
    org,
    win: { start: windowSpec.start, end: windowSpec.end },
  });
  if (!quad.length)
    return (
      <div style={{ color: "#52514e" }}>
        No {noun.many} returned an impact series for this window. Grade and
        teacher breakdowns also need learners tagged with a grade / assigned to
        classes.
      </div>
    );
  const medReach = quad.length ? quad[0].medReach : 0;
  const medDepth = quad.length ? quad[0].medDepth : 0;
  const maxDepth = quad.reduce((m, p) => Math.max(m, p.depth), 0);
  const worklist = [...quad].sort((a, b) => b.dist - a.dist);
  return (
    <FocusTab
      quad={quad}
      worklist={worklist}
      medReach={medReach}
      medDepth={medDepth}
      maxDepth={maxDepth}
      interval={interval}
      noun={noun}
      drill={drill}
      cycles={cycles}
      robotsFor={robotsFor}
    />
  );
};

// "What are students learning?" — the same concern through two lenses: where
// mastery lands in the curriculum (per-unit composition), and the stock-and-flow
// of standards accumulating across the window on a time axis ("Trajectory").
//
// It used to be three. The retired one plotted a chosen impact metric per school
// over time, which is a comparison BETWEEN PLACES rather than a reading of what
// was learned — and Adoption answers that question better now, with the reach ×
// depth quadrant, the worklist and the dosage denominators the bare lines never
// had. Trajectory covers the honest remainder: a time axis over the district's
// own mastery, at whichever grain and scope the reader picks.
//
// Both lenses are self-contained — each owns its scope controls and its own
// fetch, and is handed the page's lens tabs to host on its own ControlBar so the
// nav sits in the same place in either. This page is therefore only the toggle.
//
// Trajectory is also the district-scale mount of the figure a teacher gets for
// their class on /teacher/class-growth — same component, same fold, same
// endpoint with a wider scope. Deliberately not a second variant of the same
// picture: a principal and a teacher looking at one school in one week should be
// looking at one chart.
// The header that appears while a focus cycle is the active window. It exists
// because in-cycle numbers are selected-for by construction — the cycle was
// chosen for being the densest stretch, so measuring dosage inside it flatters
// by definition (prod: one district reads 3.0% of learners at target across the
// year and 37.6% inside its own push). Saying so at the top is the difference
// between "during your fall push" and a headline rate.
const CyclePeriodNote = ({ cycle, scope }) => (
  <div
    style={{
      display: "flex",
      gap: 10,
      alignItems: "flex-start",
      background: "rgba(192, 124, 27, 0.10)",
      borderLeft: "3px solid #b0741a",
      borderRadius: "0 8px 8px 0",
      padding: "10px 14px",
      fontSize: 13,
      marginBottom: 16,
    }}
  >
    <div>
      <strong style={{ color: "#8a5a12" }}>
        Viewing {cycle.label.toLowerCase()}
        {cycle.schoolName ? ` at ${cycle.schoolName}` : ""}
      </strong>{" "}
      — everything below describes this {Math.round(cycle.weeks)}-week push, not
      the school year. It carried {cycle.lessons.toLocaleString()} lessons across{" "}
      {cycle.active_learners.toLocaleString()} learners
      {cycle.share_of_window != null
        ? `, ${Math.round(cycle.share_of_window * 100)}% of the year's volume`
        : ""}
      .
      {scope === "dose" && (
        <>
          {" "}
          Lessons per learner are divided by the cycle's {cycle.weeks} measured
          weeks — in the mix strip and on the dose–response alike — so the bands
          spread
          further right than they do over a year. Read this as “during the push”,
          not as an ordinary rate.
        </>
      )}
    </div>
  </div>
);

const LearningPage = ({
  syncParams = () => {},
  api,
  org,
  windowSpec,
  orgunits,
  canScopeSchool,
  canPickStandardSet,
}) => {
  // Seed the lens from the URL so a link reproduces the exact Learning view,
  // and mirror each change back (state is the source of truth, the URL a
  // mirror -- same pattern as the container).
  //
  // "By curriculum" is the landing lens: the question curriculum directors
  // bring to this page is *which units/standards need attention*, and the time
  // axis answers a coarser one. `?lens=time` links from the retired third lens
  // fall through to it, which is the right landing place for someone who was
  // asking about a metric over time.
  const [lens, setLensState] = useState(() => {
    const v = new URLSearchParams(window.location.search).get("lens");
    return ["curriculum", "growth"].includes(v) ? v : "curriculum";
  });
  const setLens = (v) => {
    setLensState(v);
    syncParams({ lens: v });
  };
  // One node, handed to whichever lens is up: each renders its OWN ControlBar
  // (it owns the scope state and the fetch behind it) and hosts these tabs on
  // it, so the lens nav sits in the same place in both.
  const lensTabs = (
    <SubTabs
      value={lens}
      onChange={setLens}
      options={[
        ["curriculum", "By curriculum"],
        ["growth", "Trajectory"],
      ]}
    />
  );
  return lens === "growth" ? (
    <GroupGrowthTab
      api={api}
      org={org}
      windowSpec={windowSpec}
      orgunits={orgunits}
      canScopeSchool={canScopeSchool}
      tabs={lensTabs}
    />
  ) : (
    <UnitMasteryTab
      api={api}
      org={org}
      windowSpec={windowSpec}
      orgunits={orgunits}
      canScopeSchool={canScopeSchool}
      canPickStandardSet={canPickStandardSet}
      tabs={lensTabs}
    />
  );
};

// Adoption → Usage: the engagement mix over time + dose→response, both
// scopeable to a focus cycle.
//
// The mix strip keeps the FULL-ROSTER denominator (including "no lessons" and
// "out of session"), not an engaged-only one. The two disagree by 5x on the
// largest district (~1.6k engaged against ~8k enrolled), and the full roster is
// the honest denominator. It is also what makes the dose→response beneath it
// readable: "these gains come from this slice of 8,053".
//
// The composition strip's data (`comp`) is loaded by the container — lazily, so
// it can arrive after this lens mounts, which is what `compLoading` is for. The
// per-tier mastery fan-out is fetched here instead, because only this lens reads
// it.
//
// The dose→response reads per-subject: each subject's dose bins AND mastery are
// scoped server-side (?subject=), so a learner's math and reading doses can land
// them in different tiers. That means the segmentation itself differs by
// subject, so we fetch MATH and READING as separate bundles (in parallel) rather
// than splitting one subject-blind response. The subject-blind composition strip
// is unaffected.
const UsageLens = ({
  api,
  org,
  windowSpec,
  comp,
  compLoading,
  compFailed,
  period,
}) => {
  const [seg, setSeg] = useState({ loading: true });
  const orgId = org && org.id;
  // Scoping the dose→response to a focus cycle is the reason `cycle=` exists.
  // Over a whole year the upper dosage bands are nearly empty — the busiest
  // learner in the highest-volume district managed 4.25 lessons/week across
  // 2025-26 — so the curve has almost no horizontal spread to read a response
  // against. Inside a push those bands populate (top learners reach 7.5/wk) and
  // the plot finally has an x-axis worth regressing on.
  //
  // The catch, and why the note below says so out loud: a cycle is chosen for
  // being dense, so in-cycle dosage is selected-for. The spread is real, but it
  // is not the district's ordinary rate.
  const [grain, setGrain] = useState(null);
  const [compOverride, setCompOverride] = useState(null);
  const { start, end, interval, ssParam, utilInterval } = windowSpec;
  // The container's strip is monthly over a year, which inside an 8-week cycle
  // is two bars. Default to weekly there and let the caller override either way.
  const effInterval = grain || (period ? "week" : utilInterval);
  const compIsOurs = !!period || effInterval !== utilInterval;
  // The strip's own fetch, for when the container's bundle doesn't answer the
  // question being asked (a cycle, a different grain). A cycle detected at a
  // school has to be requested AT that school — detection runs at whatever scope
  // this endpoint is given — which also scopes the strip's roster to that
  // school, matching the `school=` the dose→response below is sent.
  useEffect(() => {
    if (!compIsOurs || !orgId) {
      setCompOverride(null);
      return;
    }
    let cancelled = false;
    setCompOverride({ loading: true });
    const school = period && period.school;
    api
      .fetchWindowedEngagement({
        scope: school ? "orgunit" : "organization",
        id: school || orgId,
        start,
        end,
        interval: effInterval,
        cycle: period ? period.key : undefined,
      })
      .then((res) => {
        if (!cancelled) setCompOverride({ loading: false, comp: res });
      })
      .catch(() => {
        if (!cancelled) setCompOverride({ loading: false, failed: true });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compIsOurs, period, orgId, start, end, effInterval]);
  useEffect(() => {
    if (!orgId) {
      setSeg({ awaitingOrg: true });
      return;
    }
    let cancelled = false;
    setSeg({ loading: true });
    // empty tiers keep their slot here (stable legend/stack order), so no
    // roster filter — unlike the school worklist
    const buildTierSchools = (res) => toSegmentRows(res && res.segments);
    const fetchSubject = (subject) =>
      api
        .fetchSegmentedWindowedImpactStats({
          dimension: "engagement",
          id: orgId,
          school: period && period.school ? period.school : undefined,
          start,
          end,
          interval,
          standardset: ssParam,
          subject,
          // The server re-derives the bounds AND swaps the dosage denominator
          // for the cycle's measured weeks. Sending dates instead would leave
          // the nominal calendar divisor in place and inflate every rate.
          cycle: period ? period.key : undefined,
        })
        .catch(() => null);
    Promise.all([fetchSubject("MATH"), fetchSubject("READING")]).then(
      ([m, r]) => {
        if (cancelled) return;
        const meta = m || r;
        setSeg({
          loading: false,
          subjectSchools: {
            MATH: buildTierSchools(m),
            READING: buildTierSchools(r),
          },
          tiers: (meta && meta.tiers) || null,
          recommendedWeekly: (meta && meta.recommended_weekly) || null,
          // Lifted alongside the tier manifest because it is the other half of
          // reading a dose bin: the manifest says where the boundaries are, this
          // says what the rates were divided by to land inside them.
          season: (meta && meta.season) || null,
        });
      }
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, start, end, interval, ssParam, period]);

  // A stale strip is worse than none here: under a cycle note, the year's
  // buckets would be read as the cycle's. So the strip blanks while its own
  // fetch is in flight (the dose→response below stays up).
  const compReady = compOverride && !compOverride.loading && !compOverride.failed;

  // Cycle ANNOTATION on the mix strip — the whole-year view's answer to "when did
  // the pushes happen", which the filter form can only answer by discarding the
  // rest of the year. Suppressed once a period IS selected: the strip is then
  // entirely inside one cycle, so shading all of it would say nothing.
  const cycles = useFocusCycles({
    api,
    org,
    win: { start, end },
    enabled: !period,
  });
  const shownComp = compIsOurs ? (compReady ? compOverride.comp : null) : comp;
  // Whose strip is on screen decides whose in-flight state describes it: our own
  // period override's, or the container's, which fetches `comp` outside the
  // first-paint barrier and so can still be working when this lens mounts.
  const mixLoading = compIsOurs
    ? !!(compOverride && compOverride.loading)
    : !!compLoading;
  const mixFailed = compIsOurs
    ? !!(compOverride && compOverride.failed)
    : !!compFailed;
  const mixBands = period
    ? null
    : cycleBandsForBuckets(shownComp && shownComp.buckets, cycles);
  const mixCycleLabel =
    cycles && cycles.length
      ? `the district's ${cycles
          .map((c) => c.label.toLowerCase())
          .join(" and ")}`
      : null;
  // The whole-year view draws the ribbon and the mix as ONE card (YearUsagePanel):
  // same weeks, one axis, mix above the per-school grid. Inside a selected cycle
  // there is nothing to merge with — the ribbon's job is showing WHERE across the
  // year the pushes are, which a window narrowed to one push cannot do — so the mix
  // falls back to its standalone form.
  //
  // Rendered while the mix is still in flight rather than only once it lands: the
  // panel has its own spinner for that, and it also holds the adoption ribbon,
  // which fetches independently and has nothing to wait for.
  const usagePanel =
    period || (!shownComp && !mixLoading && !mixFailed) ? null : (
      <YearUsagePanel
        api={api}
        org={org}
        windowSpec={windowSpec}
        comp={shownComp}
        grain={effInterval}
        onGrainChange={setGrain}
        loading={mixLoading}
        failed={mixFailed}
        cycleBands={mixBands}
        cycleLabel={mixCycleLabel}
      />
    );
  return (
    <>
      {period && <CyclePeriodNote cycle={period} scope="dose" />}
      <EngagementTab
        eng={{
          awaitingOrg: seg.awaitingOrg,
          loading: seg.loading,
          usagePanel,
          // Inside a cycle the strip has to come from our own fetch: its buckets
          // carry a dosage denominator, and the container's is the nominal
          // calendar one the cycle's measured weeks replace. Same tiers, same
          // window, two different divisors would bin the same learners
          // differently in the two halves of this page.
          comp: shownComp,
          // Grain belongs to the mix alone — it changes how that one series is
          // bucketed. The dose→response reads the window's endpoints, not its
          // buckets, so it is deliberately fetched at the container's interval
          // and doesn't re-run when this changes.
          grain: effInterval,
          onGrainChange: setGrain,
          compLoading: mixLoading,
          compFailed: mixFailed,
          subjectSchools: seg.subjectSchools,
          tiers: seg.tiers,
          recommendedWeekly: seg.recommendedWeekly,
          season: seg.season,
        }}
      />
    </>
  );
};

// "Is ABii being used, and is it working?" — the usage × impact join, through
// two lenses on the same question at different granularity.
//
//   Outliers (landing)  which units sit far from normal on reach × depth
//   Usage               how much ABii the roster is getting, and what it buys
//
// Outliers lands because the Overview's adoption widget draws the outlier
// scatter, so the click-through has to arrive on the matching picture. That
// widget deep-links ?lens=outliers explicitly rather than trusting this default.
//
// Lens state follows the Learning tab's convention: page-local state mirrored to
// ?lens= so a link reproduces the view. The param is shared with Learning's own
// lens, which is safe because each validates against its own allow-list and
// falls back to its own default — a Learning link followed into Adoption shows
// Outliers rather than nothing.
const ADOPTION_LENSES = ["outliers", "usage"];
const ADOPTION_DEFAULT_LENS = "outliers";
// Links carrying the earlier ?lens=dosage value are out in the wild, so it still
// resolves here rather than silently falling back to Outliers.
const ADOPTION_LENS_ALIASES = { dosage: "usage" };

const AdoptionPage = ({
  quad,
  interval,
  noun,
  segmentControls,
  api,
  org,
  windowSpec,
  dimension,
  comp,
  compLoading,
  compFailed,
  orgunits,
  robotsFor,
  syncParams = () => {},
}) => {
  const [lens, setLensState] = useState(() => {
    const raw = new URLSearchParams(window.location.search).get("lens");
    const v = ADOPTION_LENS_ALIASES[raw] || raw;
    return ADOPTION_LENSES.includes(v) ? v : ADOPTION_DEFAULT_LENS;
  });
  const setLens = (v) => {
    setLensState(v);
    syncParams({ lens: v });
  };
  // The focus-cycle window lives here rather than in the Usage lens because the
  // control that sets it lives in this page's bar. It is dropped on any change of
  // scope or year — "Fall" at one school is a different window than at the next,
  // so carrying the selection across would slide the bounds under an unchanged
  // label — and on leaving the lens that honours it, since Outliers deliberately
  // reads the whole window (see the note on OutliersLens).
  const [period, setPeriod] = useState(null);
  const orgId = org && org.id;
  const { start, end } = windowSpec;
  useEffect(() => {
    setPeriod(null);
  }, [orgId, start, end]);
  const pickLens = (v) => {
    if (v !== "usage") setPeriod(null);
    setLens(v);
  };
  return (
    <>
      {/* One bar: which lens on the left, and on the right the control that
          changes what data this lens is looking at. Both used to be stacked
          micro-cap fields owning a row each, which cost ~130px above the first
          plot and made a page-swapping choice look like a plot option. */}
      <ControlBar
        tabs={
          <SubTabs
            value={lens}
            onChange={pickLens}
            options={[
              ["outliers", "Outliers"],
              ["usage", "Usage"],
            ]}
          />
        }
      >
        {lens === "outliers"
          ? segmentControls({ inline: true })
          : (
            <PeriodSelect
              inline
              api={api}
              org={org}
              orgunits={orgunits}
              win={{ start, end }}
              value={period}
              onChange={setPeriod}
            />
          )}
      </ControlBar>
      {lens === "outliers" ? (
        <OutliersLens
          quad={quad}
          interval={interval}
          noun={noun}
          api={api}
          org={org}
          windowSpec={windowSpec}
          dimension={dimension}
          robotsFor={robotsFor}
        />
      ) : (
        <UsageLens
          api={api}
          org={org}
          windowSpec={windowSpec}
          comp={comp}
          compLoading={compLoading}
          compFailed={compFailed}
          period={period}
        />
      )}
    </>
  );
};

// "Is the fleet right-sized?" — deployment, capacity, worklist, renewals.
const FleetPage = ({ robotUtil }) => <RobotsTab util={robotUtil} />;

export { SegmentControls, AdoptionPage, LearningPage, FleetPage };
